import { useSetToast } from "@app/components/Toast";
import { GenerateReportModal } from "@app/screens/ExternalQuestionnaires/components/GenerateReportModal";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useSetAtom } from "jotai";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { openInactiveQuestionsModalAtom } from "../../../ctx";
import {
    useExportEvidenceFiles,
    useExportQuestionnaire,
    useGenerateAiSuggestions,
    useGenerateFrameworkReport,
} from "../../../data";
import { keys, RESOURCE_NAME } from "../../../i18n";
import { isQuestionGenerating } from "../../../services";
import {
    type ExternalQuestionnaireOptions,
    type QuestionnaireOverviewData,
} from "../../../types";
import { CreateQuestionsModal } from "../../OverviewTab/components/CreateQuestionsModal";

export const QuestionActions: FC<{
    questionnaireId: string | undefined;
    options: ExternalQuestionnaireOptions;
    questions: QuestionnaireOverviewData;
    isQuestionnaireEmpty?: boolean;
}> = ({ questionnaireId, options, questions, isQuestionnaireEmpty }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const [openGenerateReportModal, setOpenGenerateReportModal] =
        useState(false);
    const { setToastSuccess, setToastError } = useSetToast();
    const handleExportQuestionnaireClick = () => {
        if (!questionnaireId) return;
        exportQuestionnaire(
            {
                questionnaireId: questionnaireId,
            },
            {
                onError: () => {
                    setToastError(t(keys.export_questionnaire_error));
                },
            },
        );
    };

    const { generateFrameworkReport } = useGenerateFrameworkReport();

    const handleExportEvidenceFilesClick = () => {
        if (!questionnaireId) return;
        exportEvidenceFiles(questionnaireId, {
            onError: () => {
                setToastError(t(keys.export_evidence_files_error));
            },
        });
    };

    const handleGenerateReportConfirmClick = (
        selectedLocale: SupportedLocales,
    ) => {
        generateFrameworkReport(
            {
                frameworkUname: options.frameworkCmsId ?? "",
                locale: selectedLocale,
            },
            {
                onSettled(_, error) {
                    if (error) {
                        return setToastError(t(keys.generate_report_error));
                    }
                    return setToastSuccess(t(keys.generate_report_success));
                },
            },
        );
    };

    const handleGenerateReportClick = () => {
        setOpenGenerateReportModal(true);
    };

    const {
        mutate: exportQuestionnaire,
        isPending: isExportingQuestionnairePending,
    } = useExportQuestionnaire();

    const { exportEvidenceFiles, isPending: isExportingEvidenceFilesPending } =
        useExportEvidenceFiles();

    const isGenerating = useMemo(() => {
        const generatingQuestions = questions?.find((q) =>
            isQuestionGenerating(q),
        );

        return !!generatingQuestions;
    }, [questions]);

    const {
        generateAiSuggestions,
        isPending: isGeneratingAiSuggestionsPending,
    } = useGenerateAiSuggestions(questionnaireId);

    const handleSuggestAll = () => {
        if (!questionnaireId) return;
        generateAiSuggestions(questionnaireId);
    };

    const setOpenInactiveModal = useSetAtom(openInactiveQuestionsModalAtom);
    const handleSetOpenInactiveModal = () => {
        setOpenInactiveModal(true);
    };

    return (
        <FlexRow gap="3">
            {!isQuestionnaireEmpty && (
                <Dropdown>
                    <Dropdown.Trigger
                        asChild
                        id="questionnaire-more-actions-dropdown-trigger"
                    >
                        <Button
                            variant="tonal"
                            size="sm"
                            loading={
                                isExportingQuestionnairePending ||
                                isExportingEvidenceFilesPending
                            }
                            id="questionnaire-more-actions-dropdown-trigger-button"
                        >
                            <Text variant="body-sm-bold">
                                {t(keys.export_questionnaire_button_label)}
                            </Text>
                            <Button.Icon name="chevronDown" />
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Content hasPortal>
                        <Dropdown.Item onClick={handleExportQuestionnaireClick}>
                            <Dropdown.ItemIcon name="arrowDownload" />
                            <Text variant="body">
                                {t(keys.export_questionnaire)}
                            </Text>
                        </Dropdown.Item>
                        {(options.frameworkCmsId === "vsme" ||
                            options.frameworkCmsId === "mid-cap-standards") && (
                            <Dropdown.Item onClick={handleGenerateReportClick}>
                                <Dropdown.ItemIcon name="email" />
                                <Text variant="body">
                                    {t(keys.generate_report)}
                                </Text>
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={handleExportEvidenceFilesClick}>
                            <Dropdown.ItemIcon name="document" />
                            <Text variant="body">
                                {t(keys.export_evidence_files)}
                            </Text>
                        </Dropdown.Item>
                    </Dropdown.Content>
                </Dropdown>
            )}

            {options?.canEditQuestions ? (
                <CreateQuestionsModal questionnaireId={questionnaireId} />
            ) : null}

            {!isQuestionnaireEmpty && (
                <Tooltip delayDuration={0}>
                    <Tooltip.Trigger>
                        <Button
                            variant="flatAccent"
                            onClick={handleSuggestAll}
                            size="sm"
                            disabled={
                                isGenerating || isGeneratingAiSuggestionsPending
                            }
                        >
                            {t(keys.suggest_all)}
                            <Button.Icon name="stars" />
                        </Button>
                    </Tooltip.Trigger>
                    {isGenerating && (
                        <Tooltip.Content>
                            {t(keys.generating_suggestions)}
                        </Tooltip.Content>
                    )}
                </Tooltip>
            )}
            {!isQuestionnaireEmpty && (
                <GenerateReportModal
                    open={openGenerateReportModal}
                    onOpenChange={setOpenGenerateReportModal}
                    onConfirm={handleGenerateReportConfirmClick}
                />
            )}
            {options?.canSetQuestionsIrrelevant && (
                <Dropdown>
                    <Dropdown.Trigger asChild>
                        <Button variant="tonal" size="sm">
                            <Button.Icon name="more" />
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Content hasPortal>
                        <Dropdown.Item onClick={handleSetOpenInactiveModal}>
                            <Dropdown.ItemIcon name="eyeOff" />
                            <Dropdown.ItemText variant="body">
                                {t(keys.see_irrelevant_questions)}
                            </Dropdown.ItemText>
                        </Dropdown.Item>
                    </Dropdown.Content>
                </Dropdown>
            )}
        </FlexRow>
    );
};
